import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  downloadSalesCogsCsv,
  fetchSalesCogsReport,
  type SalesCogsReportResponse,
  type SalesCogsRow,
  type SalesCogsSummary,
} from '../api/reports';
import {
  estimateIncompleteCogs,
  getOrder,
  getOrderLineItems,
  type Order,
  type OrderLineItem,
} from '../api/orders';
import {
  applyManapoolOrderReports,
  fetchManapoolOrderReports,
} from '../api/manapoolOrders';
import { OrderDetailsDrawer } from '../components/orders/OrderDetailsDrawer';
import { borderRadius, colorExtensions, colors, spacing, typography, typographyFlat } from '../styles/tokens';
import { localCalendarDateIso } from '../utils/datetime';

function monthStartIso(today = localCalendarDateIso()): string {
  return `${today.slice(0, 7)}-01`;
}

function money(value?: number | null): string {
  if (value === undefined || value === null || Number.isNaN(Number(value))) {
    return '—';
  }
  return `$${Number(value).toFixed(2)}`;
}

function summaryNumber(summary: SalesCogsSummary, key: keyof SalesCogsSummary): number {
  const value = summary[key];
  return typeof value === 'number' ? value : 0;
}

export interface CkTradeinMetric {
  label: string;
  value: string;
  hint: string;
}

/** Cash payouts sit beside channel sales. Store credit stays out of revenue. */
export function ckTradeinMetrics(summary: SalesCogsSummary): CkTradeinMetric[] {
  const unpaidQty = summaryNumber(summary, 'ck_pulled_unpaid_qty');
  const copyLabel = unpaidQty === 1 ? '1 copy' : `${unpaidQty} copies`;
  return [
    {
      label: 'CK Proceeds',
      value: money(summary.ck_proceeds),
      hint: 'Cash paid in this range',
    },
    {
      label: 'CK COGS',
      value: money(summary.ck_cogs),
      hint: 'Pinned cost of cash batches',
    },
    {
      label: 'CK Batches',
      value: String(summaryNumber(summary, 'ck_batch_count')),
      hint: 'Cash batches paid in this range',
    },
    {
      label: 'CK Store Credit',
      value: money(summary.ck_credit_amount),
      hint: 'Recorded separately from sales revenue',
    },
    {
      label: 'CK Credit COGS',
      value: money(summary.ck_credit_cogs),
      hint: 'Pinned cost of credit batches',
    },
    {
      label: 'CK Credit Batches',
      value: String(summaryNumber(summary, 'ck_credit_batch_count')),
      hint: 'Credit batches paid in this range',
    },
    {
      label: 'Pulled, not yet paid',
      value: money(summary.ck_pulled_unpaid_cost),
      hint: `${copyLabel} on unpaid batches`,
    },
  ];
}

export interface OnHandValuationCopy {
  cost: string;
  qty: string;
  statuses: string[];
  note: string;
  statusFilter: string;
}

/**
 * Live acquisition cost. Shown apart from period profit.
 * Statuses come from the report; the fallback matches parity sellable
 * statuses plus ck_trade_hold.
 */
export function onHandValuationCopy(summary: SalesCogsSummary): OnHandValuationCopy {
  const fromApi = summary.on_hand_statuses?.map((status) => status.trim()).filter(Boolean);
  const statuses = fromApi && fromApi.length > 0
    ? fromApi
    : ['in_stock', 'to_list', 'listed', 'ck_trade_hold'];
  return {
    cost: money(summary.on_hand_cost),
    qty: String(summaryNumber(summary, 'on_hand_qty')),
    statuses,
    note: 'Point in time. Live acquisition cost, outside the selected date range and outside period profit.',
    statusFilter: `Status filter: ${statuses.join(', ')}`,
  };
}

/** JSON table page size. Summary KPIs and CSV stay on the full date range. */
export const SALES_COGS_PAGE_SIZE = 500;

export interface SalesCogsTableWindow {
  rangeStart: number;
  rangeEnd: number;
  totalRows: number;
  pageSize: number;
  page: number;
  totalPages: number;
  truncated: boolean;
}

export function salesCogsTableWindow(report: {
  page: number;
  page_size: number;
  total_rows: number;
}): SalesCogsTableWindow {
  const totalRows = Math.max(0, report.total_rows);
  const pageSize = Math.max(1, report.page_size);
  const page = Math.max(1, report.page);
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const rangeStart = totalRows === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = totalRows === 0 ? 0 : Math.min(page * pageSize, totalRows);
  return {
    rangeStart,
    rangeEnd,
    totalRows,
    pageSize,
    page,
    totalPages,
    truncated: totalRows > pageSize,
  };
}

export function salesCogsTableStatus(window: SalesCogsTableWindow): string {
  if (window.totalRows === 0) {
    return 'Showing 0 rows.';
  }
  if (!window.truncated) {
    return `Showing all ${window.totalRows} rows. Summary KPIs and CSV exports include this full range.`;
  }
  return (
    `Showing rows ${window.rangeStart}-${window.rangeEnd} of ${window.totalRows} ` +
    `(page size ${window.pageSize}). This table is one page of the range. ` +
    `Summary KPIs and CSV exports include all ${window.totalRows} rows.`
  );
}

function compareSalesDateDesc(a: SalesCogsRow, b: SalesCogsRow): number {
  if (a.date === b.date) return 0;
  return a.date < b.date ? 1 : -1;
}

/**
 * Incomplete COGS rows stay pinned. Within each group, newer dates come first.
 * Same-day rows keep their incoming order.
 */
export function sortSalesCogsDisplayRows(rows: SalesCogsRow[]): SalesCogsRow[] {
  const incomplete: SalesCogsRow[] = [];
  const complete: SalesCogsRow[] = [];
  for (const row of rows) {
    if (row.cogs_complete) complete.push(row);
    else incomplete.push(row);
  }
  incomplete.sort(compareSalesDateDesc);
  complete.sort(compareSalesDateDesc);
  return [...incomplete, ...complete];
}

export const ReportsSalesCogsPage: React.FC = () => {
  const [startDate, setStartDate] = useState(monthStartIso());
  const [endDate, setEndDate] = useState(localCalendarDateIso());
  const [source, setSource] = useState('');
  const [report, setReport] = useState<SalesCogsReportResponse | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [incompleteOnly, setIncompleteOnly] = useState(false);
  const reportRequest = useRef(0);

  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedLineItems, setSelectedLineItems] = useState<OrderLineItem[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const orderRequest = useRef(0);

  const summary = report?.summary;
  const tableWindow = report ? salesCogsTableWindow(report) : null;

  const displayRows = useMemo(() => {
    const rows = report?.rows ?? [];
    const filtered = incompleteOnly ? rows.filter((row) => !row.cogs_complete) : rows;
    return sortSalesCogsDisplayRows(filtered);
  }, [report?.rows, incompleteOnly]);

  const loadReport = async (event?: React.FormEvent, nextPage?: number) => {
    if (event) event.preventDefault();
    const requestedPage = event ? 1 : (nextPage ?? page);
    const requestId = ++reportRequest.current;
    try {
      setLoading(true);
      setError(null);
      const next = await fetchSalesCogsReport({
        start_date: startDate,
        end_date: endDate,
        source: source || undefined,
        page: requestedPage,
        page_size: SALES_COGS_PAGE_SIZE,
      });
      if (requestId !== reportRequest.current) return;
      setPage(next.page);
      setReport(next);
    } catch (err: unknown) {
      if (requestId !== reportRequest.current) return;
      const status = (err as { response?: { status?: number } } | null)?.response?.status;
      if (status === 403) {
        setError('Admin role required to view Sales / COGS.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to load Sales / COGS report');
      }
      setReport(null);
    } finally {
      if (requestId === reportRequest.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // Default month-to-date range only. Later filter edits stay on Load Report.
    void loadReport();
  }, []);

  const download = async (format: 'csv' | 'csv_lines') => {
    try {
      const blob = await downloadSalesCogsCsv({
        start_date: startDate,
        end_date: endDate,
        source: source || undefined,
        format,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download =
        format === 'csv_lines'
          ? `sales-cogs-lines-${startDate}-to-${endDate}.csv`
          : `sales-cogs-${startDate}-to-${endDate}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'CSV download failed');
    }
  };

  const openOrder = async (row: SalesCogsRow) => {
    const requestId = ++orderRequest.current;
    try {
      setIsLoadingOrder(true);
      setError(null);
      const [order, lineItems] = await Promise.all([
        getOrder(row.order_id),
        getOrderLineItems(row.order_id),
      ]);
      if (requestId !== orderRequest.current) return;
      setSelectedOrder(order);
      setSelectedLineItems(lineItems);
      setIsDrawerOpen(true);
    } catch (err: unknown) {
      if (requestId !== orderRequest.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load order details');
      setIsDrawerOpen(false);
      setSelectedOrder(null);
      setSelectedLineItems([]);
    } finally {
      if (requestId === orderRequest.current) {
        setIsLoadingOrder(false);
      }
    }
  };

  const closeDrawer = () => {
    orderRequest.current += 1;
    setIsDrawerOpen(false);
    setSelectedOrder(null);
    setSelectedLineItems([]);
    setIsLoadingOrder(false);
  };

  const reloadSelectedOrder = async () => {
    if (!selectedOrder) return;
    const [order, lineItems] = await Promise.all([
      getOrder(selectedOrder.id),
      getOrderLineItems(selectedOrder.id),
    ]);
    setSelectedOrder(order);
    setSelectedLineItems(lineItems);
  };

  const isManapool = selectedOrder?.source?.toLowerCase() === 'manapool';

  const metrics = useMemo(() => {
    if (!summary) return [];
    return [
      { label: 'Product Revenue', value: money(summary.product_revenue) },
      { label: 'COGS', value: money(summary.cogs) },
      { label: 'Gross Profit', value: money(summary.gross_profit) },
      { label: 'Fees', value: money(summary.fees) },
      { label: 'Net Profit', value: money(summary.net_profit) },
      { label: 'Sales / Reversals', value: `${summary.sale_count} / ${summary.reversal_count}` },
      { label: 'Shrink Qty', value: String(summary.shrink_qty ?? 0) },
    ];
  }, [summary]);

  const onHand = useMemo(() => (summary ? onHandValuationCopy(summary) : null), [summary]);
  const ckMetrics = useMemo(() => (summary ? ckTradeinMetrics(summary) : []), [summary]);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Reports: Sales / COGS</h1>
        <p style={styles.subtitle}>
          Gross merchandise profit from FIFO cost, channel fees, and event-dated reversals.
          Dates use America/Los_Angeles. Admin only. Click a row to open the order.
        </p>
      </div>

      <form style={styles.filters} onSubmit={loadReport}>
        <label style={styles.label}>
          Start
          <input
            type="date"
            style={styles.input}
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
          />
        </label>
        <label style={styles.label}>
          End
          <input
            type="date"
            style={styles.input}
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            required
          />
        </label>
        <label style={styles.label}>
          Channel
          <select
            style={styles.input}
            value={source}
            onChange={(e) => setSource(e.target.value)}
          >
            <option value="">All</option>
            <option value="manapool">Manapool</option>
            <option value="shopify">Shopify</option>
            <option value="tcgplayer">TCGPlayer</option>
          </select>
        </label>
        <button type="submit" style={styles.primaryButton} disabled={loading}>
          {loading ? 'Loading…' : 'Load Report'}
        </button>
        <button
          type="button"
          style={styles.secondaryButton}
          disabled={!report || loading}
          title="Exports every order row in the selected range, including rows not on this page"
          onClick={() => download('csv')}
        >
          Download CSV
        </button>
        <button
          type="button"
          style={styles.secondaryButton}
          disabled={!report || loading}
          title="Exports every line in the selected range, including rows not on this page"
          onClick={() => download('csv_lines')}
        >
          Line CSV
        </button>
        {report && (
          <label style={styles.checkLabel}>
            <input
              type="checkbox"
              checked={incompleteOnly}
              onChange={(e) => setIncompleteOnly(e.target.checked)}
            />
            Incomplete COGS only
          </label>
        )}
      </form>

      {error && <div style={styles.error}>{error}</div>}
      {isLoadingOrder && <div style={styles.tableStatus}>Loading order…</div>}

      {summary && (
        <>
          <section aria-label="Period profit" style={styles.metricRow}>
            {metrics.map((metric) => {
              const body = (
                <>
                  <div style={styles.metricLabel}>{metric.label}</div>
                  <div style={styles.metricValue}>{metric.value}</div>
                  {metric.label === 'Shrink Qty' && (
                    <div style={styles.metricHint}>Shrink report</div>
                  )}
                </>
              );
              if (metric.label === 'Shrink Qty') {
                return (
                  <Link
                    key={metric.label}
                    to="/reports/shrink"
                    style={{
                      ...styles.metricCard,
                      display: 'block',
                      textDecoration: 'none',
                      color: 'inherit',
                    }}
                  >
                    {body}
                  </Link>
                );
              }
              return (
                <div key={metric.label} style={styles.metricCard}>
                  {body}
                </div>
              );
            })}
          </section>
          {onHand && (
            <section aria-labelledby="on-hand-valuation-heading" style={styles.ckSection}>
              <h2 id="on-hand-valuation-heading" style={styles.ckTitle}>On-hand valuation</h2>
              <p style={styles.ckNote}>{onHand.note}</p>
              <p style={styles.ckNote}>{onHand.statusFilter}</p>
              <div style={styles.metricRow}>
                <div style={styles.metricCard}>
                  <div style={styles.metricLabel}>On-hand cost</div>
                  <div style={styles.metricValue}>{onHand.cost}</div>
                  <div style={styles.metricHint}>Point in time</div>
                </div>
                <div style={styles.metricCard}>
                  <div style={styles.metricLabel}>Copies</div>
                  <div style={styles.metricValue}>{onHand.qty}</div>
                  <div style={styles.metricHint}>Copies in the status filter</div>
                </div>
              </div>
            </section>
          )}
          <section aria-labelledby="ck-tradein-heading" style={styles.ckSection}>
            <h2 id="ck-tradein-heading" style={styles.ckTitle}>Card Kingdom trade-in</h2>
            <p style={styles.ckNote}>
              Cash payouts use the paid date in America/Los_Angeles. Store credit is listed on its own.
              Pulled cards on unpaid batches stay in the holding figure. These amounts stay out of product revenue,
              and the channel filter leaves this block unchanged.
            </p>
            <div style={styles.metricRow}>
              {ckMetrics.map((metric) => (
                <div key={metric.label} style={styles.metricCard}>
                  <div style={styles.metricLabel}>{metric.label}</div>
                  <div style={styles.metricValue}>{metric.value}</div>
                  <div style={styles.metricHint}>{metric.hint}</div>
                </div>
              ))}
            </div>
          </section>
          {tableWindow && (
            <div
              role="status"
              style={tableWindow.truncated ? styles.warn : styles.tableStatus}
            >
              {salesCogsTableStatus(tableWindow)}
            </div>
          )}
          {summary.incomplete_cogs_rows > 0 && (
            <div style={styles.warn}>
              <div>
                {summary.incomplete_cogs_rows} row(s) have incomplete COGS (shortfall, unallocated,
                or historical import without copy allocations). Highlighted rows need review —
                click a row to open the order.
              </div>
              {!incompleteOnly && (
                <button
                  type="button"
                  style={styles.warnButton}
                  onClick={() => setIncompleteOnly(true)}
                >
                  Show incomplete only
                </button>
              )}
            </div>
          )}

          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Channel</th>
                  <th style={styles.th}>External ID</th>
                  <th style={styles.th}>Revenue</th>
                  <th style={styles.th}>COGS</th>
                  <th style={styles.th}>Gross</th>
                  <th style={styles.th}>Fees</th>
                  <th style={styles.th}>Net</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.length === 0 && (
                  <tr>
                    <td style={styles.td} colSpan={10}>
                      {incompleteOnly
                        ? 'No incomplete-COGS rows in this page of results.'
                        : 'No rows for this range.'}
                    </td>
                  </tr>
                )}
                {displayRows.map((row) => {
                  const incomplete = !row.cogs_complete;
                  return (
                    <tr
                      key={`${row.row_type}-${row.order_id}-${row.date}-${row.line_id ?? ''}`}
                      style={{
                        ...styles.row,
                        ...(incomplete ? styles.incompleteRow : {}),
                      }}
                      onClick={() => openOrder(row)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          void openOrder(row);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label={`Open order ${row.external_id || row.order_id}`}
                    >
                      <td style={styles.td}>{row.row_type}</td>
                      <td style={styles.td}>{row.date}</td>
                      <td style={styles.td}>{row.channel}</td>
                      <td style={styles.td}>
                        <span style={styles.orderLink}>
                          {row.external_id || row.order_id}
                        </span>
                      </td>
                      <td style={styles.td}>{money(row.product_revenue)}</td>
                      <td style={styles.td}>{money(row.cogs)}</td>
                      <td style={styles.td}>{money(row.gross_profit)}</td>
                      <td style={styles.td}>
                        {money(row.fees)}
                        {row.fees_source === 'estimated' ? ' (est.)' : ''}
                      </td>
                      <td style={styles.td}>{money(row.net_profit)}</td>
                      <td style={styles.td}>
                        {row.commercial_status || '—'}
                        {incomplete && (
                          <span style={styles.incompleteBadge}> incomplete COGS</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {tableWindow && tableWindow.totalPages > 1 && (
            <div style={styles.pager}>
              <button
                type="button"
                aria-label="Previous page"
                style={{
                  ...styles.pagerButton,
                  ...(tableWindow.page <= 1 || loading ? styles.pagerButtonDisabled : {}),
                }}
                disabled={loading || tableWindow.page <= 1}
                onClick={() => void loadReport(undefined, tableWindow.page - 1)}
              >
                Previous
              </button>
              <span style={styles.pagerLabel}>
                Page {tableWindow.page} of {tableWindow.totalPages}
              </span>
              <button
                type="button"
                aria-label="Next page"
                style={{
                  ...styles.pagerButton,
                  ...(tableWindow.page >= tableWindow.totalPages || loading
                    ? styles.pagerButtonDisabled
                    : {}),
                }}
                disabled={loading || tableWindow.page >= tableWindow.totalPages}
                onClick={() => void loadReport(undefined, tableWindow.page + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      <OrderDetailsDrawer
        order={selectedOrder}
        lineItems={selectedLineItems}
        isOpen={isDrawerOpen}
        isLoading={isLoadingOrder}
        canImportReports={Boolean(isManapool)}
        onFetchReports={
          isManapool && selectedOrder
            ? () => fetchManapoolOrderReports(selectedOrder.external_id)
            : undefined
        }
        onApplyReports={
          isManapool && selectedOrder
            ? async (payload) => {
                await applyManapoolOrderReports(selectedOrder.external_id, payload);
                await reloadSelectedOrder();
                await loadReport();
              }
            : undefined
        }
        onEstimateIncompleteCogs={
          selectedOrder
            ? async () => {
                // Fetch reports first when Manapool so line scoping can use them.
                if (isManapool) {
                  try {
                    await fetchManapoolOrderReports(selectedOrder.external_id);
                  } catch {
                    // Cached refund_reports may still be usable.
                  }
                }
                await estimateIncompleteCogs(selectedOrder.id, {
                  prefer_reports: true,
                  apply: true,
                });
                await reloadSelectedOrder();
                await loadReport();
              }
            : undefined
        }
        onAfterUpdate={() => {
          void loadReport();
        }}
        onOrderUpdated={(updated) => {
          setSelectedOrder((current) => (
            current && current.id === updated.id ? { ...current, ...updated } : current
          ));
        }}
        onOpenPartner={async (partnerOrderId) => {
          const requestId = ++orderRequest.current;
          try {
            setIsLoadingOrder(true);
            setError(null);
            const [partner, lineItems] = await Promise.all([
              getOrder(partnerOrderId),
              getOrderLineItems(partnerOrderId),
            ]);
            if (requestId !== orderRequest.current) return;
            setSelectedOrder(partner);
            setSelectedLineItems(lineItems);
            setIsDrawerOpen(true);
          } catch (err: unknown) {
            if (requestId !== orderRequest.current) return;
            setError(err instanceof Error ? err.message : 'Failed to open partner order');
          } finally {
            if (requestId === orderRequest.current) {
              setIsLoadingOrder(false);
            }
          }
        }}
        onClose={closeDrawer}
      />
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: spacing.xl,
    maxWidth: 1400,
  },
  header: {
    marginBottom: spacing.lg,
  },
  title: {
    margin: 0,
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
  },
  subtitle: {
    marginTop: spacing.sm,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  filters: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.md,
    alignItems: 'flex-end',
    marginBottom: spacing.lg,
  },
  label: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
  },
  checkLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xs,
    fontSize: typography.small.fontSize,
    color: colors.text,
    paddingBottom: spacing.sm,
    cursor: 'pointer',
  },
  input: {
    padding: spacing.sm,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    fontSize: typography.body.fontSize,
    minWidth: 140,
  },
  primaryButton: {
    padding: `${spacing.sm} ${spacing.md}`,
    backgroundColor: colors.primary,
    color: colors.white,
    border: 'none',
    borderRadius: borderRadius.md,
    cursor: 'pointer',
    fontWeight: typographyFlat.fontWeight.medium,
  },
  secondaryButton: {
    padding: `${spacing.sm} ${spacing.md}`,
    backgroundColor: colors.backgroundAlt,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    cursor: 'pointer',
  },
  error: {
    padding: spacing.md,
    backgroundColor: colors.errorLight,
    color: colorExtensions.errorDark,
    borderRadius: borderRadius.md,
    marginBottom: spacing.md,
  },
  warn: {
    padding: spacing.md,
    backgroundColor: '#fff8e1',
    color: colors.text,
    borderRadius: borderRadius.md,
    marginBottom: spacing.md,
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.md,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tableStatus: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
    marginBottom: spacing.md,
  },
  pager: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.md,
  },
  pagerLabel: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
  },
  pagerButton: {
    padding: `${spacing.xs} ${spacing.md}`,
    backgroundColor: colors.white,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    cursor: 'pointer',
    fontSize: typography.small.fontSize,
  },
  pagerButtonDisabled: {
    backgroundColor: colors.backgroundAlt,
    color: colors.textSecondary,
    cursor: 'not-allowed',
  },
  warnButton: {
    padding: `${spacing.xs} ${spacing.md}`,
    backgroundColor: colors.white,
    color: colorExtensions.warningDark,
    border: `1px solid ${colors.warning}`,
    borderRadius: borderRadius.md,
    cursor: 'pointer',
    fontWeight: typographyFlat.fontWeight.medium,
    whiteSpace: 'nowrap',
  },
  metricRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  metricCard: {
    backgroundColor: colors.backgroundAlt,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.lg,
    padding: spacing.md,
  },
  metricLabel: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  metricValue: {
    fontSize: typography.h3.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
  },
  metricHint: {
    marginTop: spacing.xs,
    fontSize: typography.caption.fontSize,
    color: colors.textSecondary,
    lineHeight: typography.caption.lineHeight,
  },
  ckSection: {
    marginBottom: spacing.lg,
  },
  ckTitle: {
    margin: 0,
    fontSize: typography.h4.fontSize,
    fontWeight: typography.h4.fontWeight,
    color: colors.text,
  },
  ckNote: {
    marginTop: spacing.xs,
    marginBottom: spacing.md,
    color: colors.textSecondary,
    fontSize: typography.small.fontSize,
    maxWidth: 760,
  },
  tableWrap: {
    overflowX: 'auto',
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.lg,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: typography.small.fontSize,
  },
  th: {
    textAlign: 'left',
    padding: spacing.sm,
    borderBottom: `1px solid ${colors.border}`,
    backgroundColor: colors.backgroundAlt,
  },
  td: {
    padding: spacing.sm,
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'top',
  },
  row: {
    cursor: 'pointer',
  },
  incompleteRow: {
    backgroundColor: '#fff3e0',
    boxShadow: `inset 3px 0 0 ${colors.warning}`,
  },
  orderLink: {
    color: colors.primary,
    textDecoration: 'underline',
    fontWeight: typographyFlat.fontWeight.medium,
  },
  incompleteBadge: {
    color: colorExtensions.warningDark,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
};
